import {
  Box,
  Grid,
  GridItem,
  FormControl,
  FormErrorMessage,
  Input,
  FormLabel,
  Button,
  Checkbox,
} from '@chakra-ui/react';

import { Formik, Form, Field } from 'formik';
import { useRouter } from 'next/router';
import { useState } from 'react';
import * as Yup from 'yup';

const GuideSchema = Yup.object().shape({
  firstName: Yup.string()
    .min(1, 'Too Short!')
    .max(50, 'Too Long!')
    .trim()
    .required('First name is required'),
  lastName: Yup.string()
    .min(1, 'Too Short!')
    .max(50, 'Too Long!')
    .trim()
    .required('Last name is required'),
  email: Yup.string().email('Invalid email').required('Email is required'),
  subscribe: Yup.boolean(),
});

export default function GuidesEngineersForm({ conversion }: { conversion: string }) {
  const router = useRouter();
  const [submitError, setSubmitError] = useState<string>('');

  return (
    <Formik
      initialValues={{
        firstName: '',
        lastName: '',
        email: '',
        subscribe: '',
      }}
      validationSchema={GuideSchema}
      onSubmit={async (values, actions) => {
        const data = {
          ...values,
          utm_campaign: router.query.utm_campaign ?? '',
          utm_medium: router.query.utm_medium ?? '',
          utm_source: router.query.utm_source ?? '',
          utm_content: router.query.utm_content ?? '',
          utm_term: router.query.utm_term ?? '',
          form_completion_url: window.location.href,
          conversion,
        };

        try {
          const response = await fetch('/api/engineers-guides', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(data),
          });
          if (response.status === 500) {
            setSubmitError('Form submission failed. Please, try again later.');
          } else {
            actions.setSubmitting(true);
            router.push('/guides/thank-you-engineer');
          }
        } catch (err) {
          setSubmitError('Form submission failed. Please, try again later.');
        }
      }}
    >
      {({ errors, touched, isSubmitting }) => (
        <Form>
          <Grid
            templateColumns={{ base: '1fr', xl: 'repeat(2, 1fr)' }}
            gap="1rem"
            mt={{ base: '1rem', md: '0' }}
          >
            <GridItem>
              <Field name="firstName">
                {({ field }) => (
                  <FormControl
                    isInvalid={errors.firstName && touched.firstName}
                    isRequired
                    id="firstName"
                  >
                    <FormLabel fontSize="0.875rem" fontWeight="600" color="#2D445D">
                      First name
                    </FormLabel>
                    <Input
                      type="text"
                      placeholder="Enter first name"
                      borderColor="#D9D9D9"
                      borderRadius="0"
                      bgColor="#fff"
                      _placeholder={{ color: '#909090' }}
                      fontSize="0.9375rem"
                      {...field}
                    />
                    <FormErrorMessage>{errors.firstName}</FormErrorMessage>
                  </FormControl>
                )}
              </Field>
            </GridItem>
            <GridItem>
              <Field name="lastName">
                {({ field }) => (
                  <FormControl
                    isInvalid={errors.lastName && touched.lastName}
                    isRequired
                    id="lastName"
                  >
                    <FormLabel fontSize="0.875rem" fontWeight="600" color="#2D445D">
                      Last name
                    </FormLabel>
                    <Input
                      type="text"
                      placeholder="Enter last name"
                      borderColor="#D9D9D9"
                      borderRadius="0"
                      bgColor="#fff"
                      _placeholder={{ color: '#909090' }}
                      fontSize="0.9375rem"
                      {...field}
                    />
                    <FormErrorMessage>{errors.lastName}</FormErrorMessage>
                  </FormControl>
                )}
              </Field>
            </GridItem>
            <GridItem gridArea={{ xl: '2 / 1 / 3 / 3' }}>
              <Field name="email">
                {({ field }) => (
                  <FormControl isInvalid={errors.email && touched.email} isRequired id="email">
                    <FormLabel fontSize="0.875rem" fontWeight="600" color="#2D445D">
                      Email
                    </FormLabel>
                    <Input
                      type="email"
                      placeholder="Enter email"
                      borderColor="#D9D9D9"
                      borderRadius="0"
                      bgColor="#fff"
                      _placeholder={{ color: '#909090' }}
                      fontSize="0.9375rem"
                      {...field}
                    />
                    <FormErrorMessage>{errors.email}</FormErrorMessage>
                  </FormControl>
                )}
              </Field>
              <Field name="subscribe">
                {({ field }) => (
                  <FormControl mt="1rem" id="subscribe">
                    <Checkbox colorScheme="green" {...field}>
                      <Box fontSize="sm">
                        I would like to receive future communications from Terminal
                      </Box>
                    </Checkbox>
                  </FormControl>
                )}
              </Field>
              <Button
                fontFamily="body"
                fontSize="0.75rem"
                borderRadius="0.25rem"
                fontWeight="700"
                textTransform="uppercase"
                color="#fff"
                bgColor="rgba(135, 183, 92, 1)"
                minW="9.3125rem"
                mt={{ base: '1.2rem', md: '1.5rem' }}
                h="2.5rem"
                letterSpacing="0.15rem"
                _hover={{
                  backgroundColor: 'rgba(135, 183, 92, 0.8)',
                }}
                transition="all .3s ease-in-out"
                type="submit"
                isLoading={isSubmitting}
              >
                Download
              </Button>
            </GridItem>
          </Grid>
          {submitError && (
            <Box fontSize="xs" mt="1" color="red">
              {submitError}
            </Box>
          )}
        </Form>
      )}
    </Formik>
  );
}
